import { lazy, Suspense, useMemo, useRef, useState } from "react";
import { useRouter } from "@tanstack/react-router";

const FleetMap = lazy(() =>
  import("./FleetMap").then((m) => ({ default: m.FleetMap })),
);

import { PageHeader } from "../shell/PageHeader";
import {
  useGetV1Plants,
  useGetV1Heatmap,
  GetV1HeatmapMetric,
  type EnvelopePlantListDataItemsItem,
  type EnvelopePlantListDataGroupsItem,
  type Figure,
} from "@solarkey/api-client";
import {
  CardGrid,
  DataTable,
  EmptyState,
  ErrorState,
  GroupBy,
  HealthBadge,
  HealthDot,
  Metric,
  Money,
  Skeleton,
  StateWord,
  Term,
  basisLabel,
  formatAge,
  type BulkAction,
  type ColumnDef,
  type HealthModifier,
  type HealthState,
} from "@solarkey/ui";
import { useCapabilities, useHasLens } from "../session/capabilities";

import type { FleetSort, FleetView, FleetDensity } from "../router/search";

type Row = EnvelopePlantListDataItemsItem;
type Group = EnvelopePlantListDataGroupsItem;

const GROUP_DIMENSIONS = [
  { key: "region", label: "Region" },
  { key: "health", label: "Health" },
  { key: "offtaker", label: "Offtaker" },
  { key: "make", label: "Make" },
];

const SORTS: { key: FleetSort; label: string }[] = [
  // `sortCmp` orders by the raw PR figure, high to low. It is NOT a deviation from
  // the fleet (there is no server-computed fleet PR to deviate from), so the label
  // says what the sort actually does.
  { key: "pr", label: "PR, highest first" },
  { key: "at_stake", label: "Value at stake" },
  { key: "energy", label: "Energy" },
  { key: "coverage", label: "Coverage" },
  { key: "site", label: "Site name" },
];

const VIEW_OPTIONS: { key: FleetView; label: string }[] = [
  { key: "table", label: "Table" },
  { key: "cards", label: "Cards" },
  { key: "heatmap", label: "Heat map" },
  { key: "map", label: "Map" },
];

const DENSITY_OPTIONS: { key: FleetDensity; label: string }[] = [
  { key: "comfortable", label: "Comfortable" },
  { key: "compact", label: "Compact" },
];

const HEALTH_ORDER: HealthState[] = ["fault", "warning", "offline", "partial", "ok", "unmonitored"];

const HEALTH_LABELS: Record<string, string> = {
  fault: "Fault",
  warning: "Warning",
  offline: "Path down",
  partial: "Partial",
  ok: "OK",
  unmonitored: "Unmonitored",
};

// UX-REV-001 (UX-006): even 5-point buckets so the distribution is not misleading
// (the 70–76/76–80/80–84 spans were 6/4/4). The <70 low-PR threshold is preserved.
const PR_BANDS = [
  { label: "<70", min: -Infinity, max: 70 },
  { label: "70–75", min: 70, max: 75 },
  { label: "75–80", min: 75, max: 80 },
  { label: "80–85", min: 80, max: 85 },
  { label: "85+", min: 85, max: Infinity },
];

interface PlantsProps {
  tenant: string;
  scope: string;
  period?: string;
  /** Custom-window bounds (ISO dates), only meaningful when period === "custom". */
  from?: string;
  to?: string;
  view: FleetView;
  sort: FleetSort;
  filter: string;
  group: string;
  density: FleetDensity;
}

export function Plants({ tenant, scope, period, from, to, view, sort, filter, group, density }: PlantsProps): React.ReactNode {
  const router = useRouter();
  const hasValueLens = useHasLens("value");
  // Poll so the grid reflects fresh live state and new rollups (UX-005 §4.3),
  // instead of a static first load. For a custom window, forward from/to — the
  // handler has always accepted them; without them a custom range resolves to
  // the live day (every period but custom ignores them, so send only then).
  const query = useGetV1Plants(
    { period, ...(period === "custom" ? { from, to } : {}) },
    { query: { refetchInterval: 300_000 } },
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [healthFilter, setHealthFilter] = useState<HealthState | null>(null);
  const [prBandFilter, setPrBandFilter] = useState<number | null>(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsBtnRef = useRef<HTMLButtonElement>(null);
  const [columnsPos, setColumnsPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const [viewsOpen, setViewsOpen] = useState(false);
  const viewsBtnRef = useRef<HTMLButtonElement>(null);
  const [viewsPos, setViewsPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });

  const toggleViews = () => {
    if (!viewsOpen && viewsBtnRef.current) {
      const r = viewsBtnRef.current.getBoundingClientRect();
      setViewsPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
    }
    setViewsOpen((v) => !v);
  };
  const [columnSel, setColumnSel] = useState<Set<string>>(loadColumnSelection);
  const [activeSet, setActiveSet] = useState("monitoring");
  const caps = useCapabilities();

  const toggleColumns = () => {
    if (!columnsOpen && columnsBtnRef.current) {
      const r = columnsBtnRef.current.getBoundingClientRect();
      setColumnsPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
    }
    setColumnsOpen((v) => !v);
  };

  const toggleColumn = (key: string) => {
    setColumnSel((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      saveColumnSelection(next);
      return next;
    });
  };

  const applyColumnSet = (setKey: string) => {
    setActiveSet(setKey);
    const next = new Set(COLUMN_SET_DEFAULTS[setKey] ?? []);
    setColumnSel(next);
    saveColumnSelection(next);
  };

  const visibleColIds = useMemo(() => computeVisibleColIds(columnSel), [columnSel]);

  // Discrete choices (view, sort, group, density) PUSH, so Back undoes the one the
  // reader just made instead of leaving the fleet grid (QA, 25 Oct). The find box
  // passes `replace` because it fires per keystroke and would otherwise bury the
  // previous screen under one history entry per character.
  const setSearch = (patch: Record<string, string>, opts?: { replace?: boolean }) => {
    void router.navigate({
      search: ((prev: Record<string, unknown>) => ({ ...prev, ...patch })) as never,
      replace: opts?.replace ?? false,
    });
  };

  const envelope = query.data?.status === 200 ? query.data.data : null;
  const allRows: Row[] = useMemo(() => envelope?.data?.items ?? [], [envelope]);
  const groups: Group[] = useMemo(() => envelope?.data?.groups ?? [], [envelope]);
  // The server returns the fleet's ABSOLUTE PR reference (energy-weighted ratio of
  // sums — the same figure Home and Performance show). The PR-distribution caption
  // used to hard-code "Cannot check" and ignore it, so Plants disagreed with every
  // other page (QA). Read and show the served figure.
  const fleetPrRef = (envelope?.data as unknown as
    { fleet?: { pr_reference?: Figure } } | undefined)?.fleet?.pr_reference;

  const healthCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of allRows) {
      const s = (r.health?.state as string) ?? "unmonitored";
      counts[s] = (counts[s] ?? 0) + 1;
    }
    return counts;
  }, [allRows]);

  const prBandCounts = useMemo(() => {
    const counts = PR_BANDS.map(() => 0);
    let noFigure = 0;
    for (const r of allRows) {
      const v = figVal(r.pr);
      if (v < 0) { noFigure++; continue; }
      for (let i = 0; i < PR_BANDS.length; i++) {
        const b = PR_BANDS[i]; if (b && v >= b.min && v < b.max) { counts[i] = (counts[i] ?? 0) + 1; break; }
      }
    }
    return { counts, noFigure };
  }, [allRows]);

  const rows = useMemo(() => {
    let filtered = allRows;
    if (healthFilter) {
      filtered = filtered.filter((r) => (r.health?.state ?? "unmonitored") === healthFilter);
    }
    if (prBandFilter != null) {
      const band = PR_BANDS[prBandFilter];
      if (band) {
        const bMin = band.min, bMax = band.max;
        filtered = filtered.filter((r) => {
          const v = figVal(r.pr);
          return v >= 0 && v >= bMin && v < bMax;
        });
      }
    }
    const q = filter.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter(
        (r) =>
          (r.site_id ?? "").toLowerCase().includes(q) ||
          (r.tenant_name ?? "").toLowerCase().includes(q) ||
          (r.route ?? "").toLowerCase().includes(q),
      );
    }
    return [...filtered].sort((a, b) => sortCmp(a, b, sort));
  }, [allRows, filter, sort, healthFilter, prBandFilter]);

  const [savedViews, setSavedViews] = useState<FleetSavedView[]>(loadSavedViews);
  const [activeViewId, setActiveViewId] = useState<string | undefined>(undefined);

  const selectedSites = useMemo(
    () => rows.filter((r) => selected.has(r.site_id ?? "")),
    [rows, selected],
  );

  // No fleet PR is computed here. PR is a ruled KPI (SKY-INTERNAL-KPIDEF-001) and
  // the KPI engine owns it (CLAUDE.md rule 2) — a browser-side roll-up of the per-site
  // figures is a second, unruled implementation of it. The fleet figure is WITHHELD
  // until the plant-list contract carries a server-computed roll-up.

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-density-pad" aria-busy="true">
        <Skeleton variant="text" width="10rem" />
        <Skeleton variant="row" lines={8} />
      </div>
    );
  }
  if (query.isError || !query.data || query.data.status !== 200) {
    return (
      <ErrorState
        title="Sites unavailable"
        message="The fleet grid could not be loaded."
        onRetry={() => void query.refetch()}
      />
    );
  }

  const totalCapacityMW = allRows.reduce((sum, r) => {
    const f = r.capacity_dc;
    if (!f || typeof f.value !== "number") return sum;
    const rule = f.unit ? UNIT_SCALE[f.unit] : undefined;
    return sum + (rule ? f.value / rule.divisor : f.value);
  }, 0);
  const capacityLabel = totalCapacityMW >= 1000
    ? `${formatNum(totalCapacityMW / 1000)} GWp`
    : `${formatNum(totalCapacityMW)} MWp`;
  const sortLabel = SORTS.find((s) => s.key === sort)?.label ?? "PR, highest first";

  const toggleSelect = (siteId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.site_id ?? "")));
  };

  const onSaveView = () => {
    const defaultName = `View ${savedViews.length + 1}`;
    const raw = (typeof window !== "undefined" && window.prompt?.("Name this view", defaultName)) || defaultName;
    const name = raw.trim() || defaultName;
    // Saving over an existing name updates that view instead of duplicating it.
    const existing = savedViews.find((v) => v.name.toLowerCase() === name.toLowerCase());
    const newView: FleetSavedView = {
      id: existing?.id ?? `sv_${Date.now()}`,
      name: existing?.name ?? name,
      search: { view, sort, group, density, filter },
      columns: [...columnSel],
    };
    const updated = existing
      ? savedViews.map((v) => (v.id === existing.id ? newView : v))
      : [...savedViews, newView];
    setSavedViews(updated);
    setActiveViewId(newView.id);
    saveSavedViews(updated);
  };

  const onSelectView = (id: string) => {
    const v = savedViews.find((x) => x.id === id);
    if (!v) return;
    setActiveViewId(id);
    const cols = new Set(v.columns);
    setColumnSel(cols);
    saveColumnSelection(cols);
    setSearch(v.search);
  };

  const onDeleteView = (id: string) => {
    const updated = savedViews.filter((v) => v.id !== id);
    setSavedViews(updated);
    saveSavedViews(updated);
    if (activeViewId === id) setActiveViewId(undefined);
  };

  const exportCsv = () => {
    const header = ["Site", "Capacity", "Health", "Energy", "Basis", "PR", "Coverage", "Alerts", "Seen"];
    const csvRows = rows.map((r) => [
      r.site_id ?? "",
      csvNum(r.capacity_dc),
      r.health?.state ?? "",
      csvNum(r.energy_today),
      r.energy_today?.basis ? basisLabel(r.energy_today.basis) : r.energy_today?.state ?? "",
      csvNum(r.pr),
      csvNum(r.coverage),
      // Blank, not "0" — an absent count is unknown, not "no alerts".
      typeof r.open_interventions === "number" ? String(r.open_interventions) : "",
      r.freshness?.age_s != null ? formatAge(r.freshness.age_s) : "",
    ]);
    // RFC 4180: every field is quoted and internal quotes doubled, so a value like
    // "8400" formatted with a thousands separator (or a name/label with a comma)
    // can never split into two columns the way the old unquoted join did (QA PL1).
    const csv = [header, ...csvRows]
      .map((row) => row.map(csvField).join(","))
      .join("\r\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `plants-${period ?? "today"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isGrouped = group !== "" && group !== undefined;
  const compact = density === "compact";

  const bulkActions: BulkAction[] = [
    { key: "compare", label: "Compare", onRun: () => {} },
    { key: "export", label: "Export CSV", onRun: exportCsv },
    { key: "work", label: "Create work orders · Easy T", onRun: () => {} },
  ];

  return (
    <div className="flex flex-col gap-density-pad">
      <PageHeader
        title="Plants"
        scope={`${allRows.length} ${allRows.length === 1 ? "site" : "sites"} · ${capacityLabel} · ranked by ${sortLabel}`}
        timeOptions={["today", "7d", "30d", "custom"]}
        showDateNav
        action={
          <button type="button" className="sk-btn sk-btn--outline" onClick={exportCsv}>
            Export CSV
          </button>
        }
      />

      {/* ── Health status bar (annotation 1) ── */}
      <div className="sk-health-bar" role="group" aria-label="Filter by health">
        <button
          type="button"
          className={`sk-health-bar__seg${healthFilter === null ? " sk-health-bar__seg--on" : ""}`}
          onClick={() => setHealthFilter(null)}
        >
          <span className="sk-health-bar__label">All</span>
          <span className="sk-health-bar__count">{allRows.length}</span>
        </button>
        {HEALTH_ORDER
          // Show the full set of health states like the wireframe (annotation 1), so an
          // absent state still reads as "0"; hide only "unmonitored" when it has none.
          .filter((s) => s !== "unmonitored" || (healthCounts[s] ?? 0) > 0)
          .map((state) => {
            const count = healthCounts[state] ?? 0;
            return (
              <button
                key={state}
                type="button"
                className={`sk-health-bar__seg${healthFilter === state ? " sk-health-bar__seg--on" : ""}${count === 0 ? " sk-health-bar__seg--empty" : ""}`}
                onClick={() => count > 0 && setHealthFilter(healthFilter === state ? null : state)}
                aria-disabled={count === 0}
              >
                <HealthDot state={state} />
                <span className="sk-health-bar__label">{HEALTH_LABELS[state] ?? state}</span>
                <span className="sk-health-bar__count">{count}</span>
              </button>
            );
          })}

        {/* Group-by shortcuts. These set Group-by, they do NOT filter — "+ Region"
            read like "add a filter" (QA P3), so they say "Group: …" and show the
            active one pressed. The dead "+ Capacity band" / "+ Flags" buttons (no
            handler, not real group dimensions) are removed, as the other dead
            controls were. */}
        {GROUP_DIMENSIONS.filter((d) => d.key !== "health").map((d) => (
          <button
            key={d.key}
            type="button"
            className={`sk-health-bar__add${group === d.key ? " on" : ""}`}
            aria-pressed={group === d.key}
            title={`Group the fleet by ${d.label.toLowerCase()}`}
            onClick={() => setSearch({ group: group === d.key ? "" : d.key })}
          >
            Group: {d.label}
          </button>
        ))}

        <input
          type="search"
          value={filter}
          onChange={(e) => setSearch({ filter: e.target.value }, { replace: true })}
          placeholder="Find site, identifier, region"
          aria-label="Find a site"
          className="sk-toolbar__find"
          style={{ marginLeft: "auto" }}
        />
      </div>

      {/* ── Toolbar (wireframe 08) ── */}
      <div className="sk-toolbar" style={{ flexWrap: "wrap" }}>
        <div className="sk-viewtabs" role="group" aria-label="View mode">
          {VIEW_OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              className={`sk-viewtab${view === o.key ? " sk-viewtab--on" : ""}`}
              onClick={() => setSearch({ view: o.key })}
              aria-pressed={view === o.key}
            >
              {o.label}
            </button>
          ))}
        </div>

        <GroupBy
          dimensions={GROUP_DIMENSIONS}
          value={group || undefined}
          onChange={(v) => setSearch({ group: v ?? "" })}
        />

        <label className="sk-groupby">
          <span className="sk-groupby__label">Sort</span>
          <select
            className="sk-groupby__select"
            value={sort}
            onChange={(e) => setSearch({ sort: e.target.value })}
            aria-label="Sort sites"
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </label>

        <div className="sk-densitytoggle">
          <ToggleGroup
            options={DENSITY_OPTIONS}
            value={density}
            onChange={(v) => setSearch({ density: v })}
            label="Density"
          />
        </div>

        <div className="ml-auto" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            ref={viewsBtnRef}
            type="button"
            className="sk-groupby__select"
            style={{ fontSize: 10 }}
            onClick={toggleViews}
            aria-expanded={viewsOpen}
            aria-haspopup="menu"
          >
            Saved views{savedViews.length > 0 ? ` (${savedViews.length})` : ""} ▾
          </button>
          <button
            ref={columnsBtnRef}
            type="button"
            className="sk-groupby__select"
            style={{ fontSize: 10 }}
            onClick={toggleColumns}
            aria-expanded={columnsOpen}
            aria-haspopup="menu"
          >
            Columns ▾
          </button>
        </div>
      </div>

      {/* ── Saved views — dropdown under the Saved views button ── */}
      {viewsOpen && (
        <SavedViewsMenu
          pos={viewsPos}
          views={savedViews}
          activeId={activeViewId}
          onSelect={(id) => { onSelectView(id); setViewsOpen(false); }}
          onDelete={onDeleteView}
          onSave={() => { onSaveView(); setViewsOpen(false); }}
          onClose={() => setViewsOpen(false)}
        />
      )}

      {/* ── Columns menu (annotation 2) — opens under the Columns button ── */}
      {columnsOpen && (
        <ColumnsMenu
          pos={columnsPos}
          onClose={() => setColumnsOpen(false)}
          tier={caps.tier}
          columnSel={columnSel}
          activeSet={activeSet}
          onToggle={toggleColumn}
          onSelectSet={applyColumnSet}
        />
      )}

      {/* ── PR distribution (annotation 3) ── */}
      {view === "table" && (
        <div className="sk-pr-dist">
          <div className="sk-pr-dist__header">
            <span className="font-medium">PR distribution</span>
            <span className="text-text-muted" style={{ fontSize: 11 }}>
              Absolute · {period ?? "today"} · sites by PR %
            </span>
          </div>
          <div className="sk-pr-dist__bars" role="group" aria-label="PR distribution: number of sites in each PR % band">
            {PR_BANDS.map((band, i) => {
              const count = prBandCounts.counts[i] ?? 0;
              const maxCount = Math.max(...prBandCounts.counts, 1);
              const pct = (count / maxCount) * 100;
              return (
                <button
                  key={band.label}
                  type="button"
                  className={`sk-pr-dist__band${prBandFilter === i ? " sk-pr-dist__band--on" : ""}`}
                  onClick={() => setPrBandFilter(prBandFilter === i ? null : i)}
                  aria-pressed={prBandFilter === i}
                >
                  <div className="sk-pr-dist__bar-wrap">
                    <span className={`sk-pr-dist__val${count === 0 ? " sk-pr-dist__val--zero" : ""}`}>
                      {count}
                    </span>
                    <div
                      className="sk-pr-dist__bar"
                      style={{ height: count > 0 ? `${Math.max(pct, 8)}%` : "0%" }}
                    />
                  </div>
                  <span className="sk-pr-dist__label">{band.label}</span>
                </button>
              );
            })}
          </div>
          <div className="sk-pr-dist__caption">
            Fleet PR{" "}
            {typeof fleetPrRef?.value === "number"
              ? <Metric figure={fleetPrRef} showBasis={false} showProvenance={false}
                        showFreshness={false} className="sk-metric--table" />
              : <StateWord
                  state={fleetPrRef?.state ?? "CANNOT_CHECK"}
                  reason={fleetPrRef?.reason ?? { template: "CANNOT_CHECK_NO_FLEET_ROLLUP" }}
                />}
            {prBandCounts.noFigure > 0
              ? ` · ${prBandCounts.noFigure} ${prBandCounts.noFigure === 1 ? "site" : "sites"} Cannot check`
              : ""}
            {prBandFilter != null ? " · a band filters the grid" : ""}
          </div>
        </div>
      )}

      {/* ── Bulk bar (annotation 5) — dark strip above the grid ── */}
      {selected.size > 0 && (
        <div className="sk-fleet-bulk" role="toolbar" aria-label="Bulk actions">
          <span className="sk-fleet-bulk__count">
            {selected.size} selected
            {selectedSites.length === 1 && selectedSites[0]
              ? ` · ${selectedSites[0].site_id}`
              : ""}
          </span>
          <div className="sk-fleet-bulk__actions">
            {bulkActions.map((a) => (
              <button key={a.key} type="button" className="sk-fleet-bulk__action" onClick={a.onRun}>
                {a.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="sk-fleet-bulk__clear"
            onClick={() => setSelected(new Set())}
          >
            Clear
          </button>
        </div>
      )}

      {/* ── Content area ── */}
      {rows.length === 0 ? (
        <EmptyState variant="no_data" title="No sites in scope" />
      ) : view === "map" ? (
        <Suspense fallback={<Skeleton variant="row" lines={6} />}>
          <FleetMap rows={rows} onOpen={(id) => openRow(id)} />
        </Suspense>
      ) : view === "cards" ? (
        <CardsView rows={rows} grouped={isGrouped} group={group} onOpen={openRow} />
      ) : view === "heatmap" ? (
        <HeatmapView rows={rows} period={period} from={from} to={to} />
      ) : isGrouped ? (
        <GroupedTable
          rows={rows}
          groups={groups}
          group={group}
          selected={selected}
          onToggle={toggleSelect}
          onToggleAll={toggleAll}
          hasValueLens={hasValueLens}
          compact={compact}
          onOpen={openRow}
          totalSites={allRows.length}
          visibleColIds={visibleColIds}
        />
      ) : (
        <div className={`sk-fleet-table${compact ? " sk-fleet-table--compact" : ""}`}>
          <DataTable<Row>
            columns={makeColumns(hasValueLens, selected, toggleSelect, toggleAll, rows.length, visibleColIds)}
            rows={rows}
            getRowId={(r, i) => r.site_id ?? String(i)}
            onRowClick={(r) => openRow(r.site_id ?? undefined)}
            estimateRowHeight={compact ? 32 : 44}
            emptyState={<EmptyState variant="no_data" title="No sites in scope" />}
          />
          <FleetFooter total={allRows.length} shown={rows.length} />
        </div>
      )}
    </div>
  );

  function openRow(siteId?: string) {
    if (!siteId) return;
    void router.navigate({
      to: "/t/$tenant/s/$scope/plants/$site/now" as never,
      params: { tenant, scope, site: siteId } as never,
      search: { period: "today", mode: "live" } as never,
    });
  }
}

/* ════════════════════════════════════════════════════════════════════════════
   Toggle button group (view mode / density)
   ════════════════════════════════════════════════════════════════════════════ */

function ToggleGroup<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { key: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}): React.ReactNode {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex items-center gap-0.5 rounded-lg border border-border p-0.5"
    >
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          aria-pressed={o.key === value}
          onClick={() => onChange(o.key)}
          className={`rounded-md px-2 py-0.5 text-xs font-medium transition-colors focus:outline focus:outline-2 focus:outline-accent ${
            o.key === value ? "bg-accent text-white" : "text-text-muted hover:bg-surface-hover"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Columns menu (annotation 2) — column sets with checkboxes
   ════════════════════════════════════════════════════════════════════════════ */

const COLUMN_SETS: { key: string; label: string }[] = [
  { key: "monitoring", label: "Monitoring" },
  { key: "performance", label: "Performance" },
  { key: "data_quality", label: "Data quality" },
];

interface ColDef {
  key: string;
  label: string;
  unit: string;
  /** Table column ids this toggle shows/hides. Empty = no column to show yet. */
  colIds: string[];
  /** Tier required to enable it; below that it is shown locked and uncheckable. */
  lockedTier?: "easy_m" | "easy_t" | "easy_u";
}

/* Column catalogue. Order is row-major across the menu's two columns, so this
   order reproduces the wireframe layout (power | energy, yield | cuf, …). */
const COL_DEFS: ColDef[] = [
  { key: "power", label: "Power", unit: "MW", colIds: ["power"] },
  { key: "energy", label: "Energy, with basis", unit: "MWh", colIds: ["energy_today", "basis"] },
  { key: "sp_yield", label: "Specific yield", unit: "kWh/kWp", colIds: ["sp_yield"] },
  { key: "cuf", label: "CUF", unit: "%", colIds: ["cuf"] },
  { key: "pr", label: "PR", unit: "%", colIds: ["pr"] },
  { key: "availability", label: "Availability", unit: "%", colIds: ["availability"] },
  { key: "insolation", label: "Insolation", unit: "kWh/m²", colIds: ["insolation"] },
  // No field on the plant-list contract carries either of these, so there is no
  // column to show. Empty colIds puts them in the menu as disabled ("No data for
  // this column yet") instead of adding a column that renders a dash forever.
  { key: "module_temp", label: "Module temperature", unit: "°C", colIds: [] },
  { key: "inv_reporting", label: "Inverters reporting", unit: "n / N", colIds: [] },
  { key: "coverage", label: "Coverage", unit: "%", colIds: ["coverage"] },
  { key: "alerts", label: "Alerts", unit: "open", colIds: ["alerts"] },
  { key: "last_packet", label: "Last packet", unit: "age", colIds: ["seen"] },
  { key: "capacity", label: "Capacity", unit: "MWp", colIds: ["capacity"] },
  { key: "ambient_temp", label: "Ambient temperature", unit: "°C", colIds: [] },
  { key: "co2", label: "CO₂ avoided", unit: "t", colIds: [] },
  { key: "easy_t", label: "Energy at stake · Easy T", unit: "MWh", colIds: ["value"], lockedTier: "easy_t" },
];

/* Which column keys each set turns on. Switching sets applies these. */
const COLUMN_SET_DEFAULTS: Record<string, string[]> = {
  monitoring: ["power", "energy", "sp_yield", "cuf", "pr", "availability", "insolation", "coverage", "alerts", "last_packet"],
  performance: ["power", "energy", "sp_yield", "pr", "cuf", "availability"],
  data_quality: ["coverage", "last_packet", "insolation", "energy"],
};

/** Columns that are always present, outside the toggle set. */
const ALWAYS_ON_COL_IDS = new Set(["select", "site_id", "health"]);

const TIER_RANK: Record<string, number> = { easy_m: 0, easy_t: 1, easy_u: 2 };
function tierMeets(have: string, need: string): boolean {
  return (TIER_RANK[have] ?? 0) >= (TIER_RANK[need] ?? 0);
}

const COLUMNS_STORAGE_KEY = "sk.fleet.columns";

function loadColumnSelection(): Set<string> {
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch { /* storage unavailable */ }
  return new Set(COLUMN_SET_DEFAULTS.monitoring);
}

function saveColumnSelection(sel: Set<string>): void {
  try {
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify([...sel]));
  } catch { /* storage unavailable */ }
}

function computeVisibleColIds(sel: Set<string>): Set<string> {
  const ids = new Set(ALWAYS_ON_COL_IDS);
  for (const def of COL_DEFS) {
    if (sel.has(def.key)) def.colIds.forEach((id) => ids.add(id));
  }
  return ids;
}

/** A saved view captures the whole grid configuration, not just a name. */
interface FleetSavedView {
  id: string;
  name: string;
  search: Record<string, string>;
  columns: string[];
}

const SAVED_VIEWS_STORAGE_KEY = "sk.fleet.savedViews";

function loadSavedViews(): FleetSavedView[] {
  try {
    const raw = localStorage.getItem(SAVED_VIEWS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    // Tolerate the old {id,name}-only shape.
    return Array.isArray(parsed)
      ? parsed.map((v) => ({
          id: String((v as FleetSavedView).id),
          name: String((v as FleetSavedView).name),
          search: (v as FleetSavedView).search ?? {},
          columns: (v as FleetSavedView).columns ?? [...(COLUMN_SET_DEFAULTS.monitoring ?? [])],
        }))
      : [];
  } catch {
    return [];
  }
}

function saveSavedViews(views: FleetSavedView[]): void {
  try {
    localStorage.setItem(SAVED_VIEWS_STORAGE_KEY, JSON.stringify(views));
  } catch { /* storage unavailable */ }
}

function SavedViewsMenu({
  pos,
  views,
  activeId,
  onSelect,
  onDelete,
  onSave,
  onClose,
}: {
  pos: { top: number; right: number };
  views: FleetSavedView[];
  activeId?: string;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onSave: () => void;
  onClose: () => void;
}): React.ReactNode {
  return (
    <>
      <button
        type="button"
        aria-label="Close saved views"
        style={{ position: "fixed", inset: 0, background: "transparent", border: 0, zIndex: 40 }}
        onClick={onClose}
      />
      <div
        className="sk-views-menu"
        style={{ position: "fixed", top: pos.top, right: pos.right, zIndex: 41 }}
        role="menu"
      >
        {views.length === 0 ? (
          <div className="sk-views-menu__empty">No saved views yet.</div>
        ) : (
          views.map((v) => (
            <div key={v.id} className={`sk-views-menu__row${v.id === activeId ? " sk-views-menu__row--on" : ""}`}>
              <button type="button" className="sk-views-menu__pick" onClick={() => onSelect(v.id)}>
                {v.name}
              </button>
              <button
                type="button"
                className="sk-views-menu__del"
                aria-label={`Delete ${v.name}`}
                title="Delete view"
                onClick={() => onDelete(v.id)}
              >
                &times;
              </button>
            </div>
          ))
        )}
        <button type="button" className="sk-views-menu__save" onClick={onSave}>
          + Save current view
        </button>
      </div>
    </>
  );
}

function ColumnsMenu({
  pos,
  onClose,
  tier,
  columnSel,
  activeSet,
  onToggle,
  onSelectSet,
}: {
  pos: { top: number; right: number };
  onClose: () => void;
  tier: string;
  columnSel: Set<string>;
  activeSet: string;
  onToggle: (key: string) => void;
  onSelectSet: (setKey: string) => void;
}): React.ReactNode {
  return (
    <>
      <button
        type="button"
        aria-label="Close columns menu"
        style={{ position: "fixed", inset: 0, background: "transparent", border: 0, zIndex: 40 }}
        onClick={onClose}
      />
      <div
        className="sk-col-menu"
        style={{ position: "fixed", top: pos.top, right: pos.right, zIndex: 41, width: 460, maxWidth: "calc(100vw - 32px)" }}
      >
        <div className="sk-col-menu__header">
          <span className="font-medium">Columns menu, open</span>
          <span className="text-text-muted" style={{ fontSize: 11, marginLeft: 8 }}>sets, then columns</span>
        </div>

        <div className="sk-col-menu__sets" role="group" aria-label="Column sets">
          {COLUMN_SETS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`sk-col-menu__set${activeSet === s.key ? " sk-col-menu__set--on" : ""}`}
              onClick={() => onSelectSet(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="sk-col-menu__cols">
          {COL_DEFS.map((col) => {
            const locked = col.lockedTier ? !tierMeets(tier, col.lockedTier) : false;
            const unavailable = col.colIds.length === 0;
            const disabled = locked || unavailable;
            return (
              <label
                key={col.key}
                className={`sk-col-menu__col${disabled ? " sk-col-menu__col--disabled" : ""}`}
                title={locked ? "Needs a higher tier" : unavailable ? "No data for this column yet" : undefined}
              >
                <input
                  type="checkbox"
                  checked={columnSel.has(col.key) && !disabled}
                  disabled={disabled}
                  onChange={() => onToggle(col.key)}
                />
                <span>{col.label}</span>
                <span className="text-text-muted" style={{ fontSize: 10, marginLeft: "auto" }}>{col.unit}</span>
              </label>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Grouped table (annotation 2, 5)
   ════════════════════════════════════════════════════════════════════════════ */

function GroupedTable({
  rows,
  groups,
  group,
  selected,
  onToggle,
  onToggleAll,
  hasValueLens,
  compact,
  onOpen,
  totalSites,
  visibleColIds,
}: {
  rows: Row[];
  groups: Group[];
  group: string;
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  hasValueLens: boolean;
  compact: boolean;
  onOpen: (id?: string) => void;
  totalSites: number;
  visibleColIds?: Set<string>;
}): React.ReactNode {
  const grouped = useMemo(() => {
    if (groups.length > 0) {
      const buckets = new Map<string, Row[]>();
      for (const r of rows) {
        const key = r.grouping_values?.[group] ?? "Other";
        (buckets.get(key) ?? buckets.set(key, []).get(key)!).push(r);
      }
      return groups.map((g) => ({
        group: g,
        label: g.label ?? "Other",
        rows: buckets.get(g.label ?? "Other") ?? [],
      }));
    }
    if (group === "health") {
      return groupByHealth(rows).map(([state, groupRows]) => ({
        group: null as Group | null,
        label: state,
        rows: groupRows,
      }));
    }
    const buckets = new Map<string, Row[]>();
    for (const r of rows) {
      const key = r.grouping_values?.[group] ?? "Other";
      (buckets.get(key) ?? buckets.set(key, []).get(key)!).push(r);
    }
    return Array.from(buckets.entries()).map(([label, groupRows]) => ({
      group: null as Group | null,
      label,
      rows: groupRows,
    }));
  }, [rows, groups, group]);

  const cols = makeColumns(hasValueLens, selected, onToggle, onToggleAll, rows.length, visibleColIds);

  return (
    <div className={`sk-fleet-table${compact ? " sk-fleet-table--compact" : ""}`}>
      {grouped.map(({ group: g, label, rows: groupRows }) => {
        const groupCapMW = groupRows.reduce((s, r) => {
          const f = r.capacity_dc;
          if (!f || typeof f.value !== "number") return s;
          const rule = f.unit ? UNIT_SCALE[f.unit] : undefined;
          return s + (rule ? f.value / rule.divisor : f.value);
        }, 0);
        const capLabel = groupCapMW >= 1000
          ? `${formatNum(groupCapMW / 1000)} GWp`
          : `${formatNum(groupCapMW)} MWp`;

        const pathsDown = g?.paths_down ?? groupRows.filter((r) => r.freshness?.state === "path_down").length;
        // Only rows that actually carry a count contribute; rows without one are
        // reported as unknown rather than folded in as 0.
        const alertKnown = groupRows.filter((r) => typeof r.open_interventions === "number");
        const alertCount = alertKnown.reduce((s, r) => s + (r.open_interventions as number), 0);
        const alertUnknown = groupRows.length - alertKnown.length;
        const quietCount = g?.quiet_count ?? 0;
        const visibleRows = quietCount > 0 ? groupRows.slice(0, groupRows.length - quietCount) : groupRows;

        return (
          <section key={label} className="sk-fleet-group">
            <div className="sk-fleet-group__header">
              {group === "health" ? (
                <HealthBadge state={label as HealthState} showLabel />
              ) : (
                <span className="font-semibold">{label}</span>
              )}
              <span className="text-text-muted">
                {" · "}{groupRows.length} {groupRows.length === 1 ? "site" : "sites"}
                {" · "}{capLabel}
                {" · "}PR{" "}
                <StateWord
                  state="CANNOT_CHECK"
                  reason={{ template: "CANNOT_CHECK_NO_GROUP_ROLLUP" }}
                />
              </span>
              {pathsDown > 0 && (
                <span style={{ color: "var(--sk-color-health-offline)" }}>
                  {" · "}{pathsDown} {pathsDown === 1 ? "path" : "paths"} down
                </span>
              )}
              {alertCount > 0 && (
                <span className="text-text-muted">
                  {" · "}{alertCount} {alertCount === 1 ? "alert" : "alerts"}
                </span>
              )}
              {alertUnknown > 0 && (
                <span className="text-text-muted">
                  {" · "}{alertUnknown} {alertUnknown === 1 ? "site" : "sites"} alert count{" "}
                  <StateWord
                    state="CANNOT_CHECK"
                    reason={{ template: "CANNOT_CHECK_NO_INTERVENTION_COUNT" }}
                  />
                </span>
              )}
            </div>

            <DataTable<Row>
              columns={cols}
              rows={visibleRows}
              getRowId={(r, i) => r.site_id ?? String(i)}
              onRowClick={(r) => onOpen(r.site_id ?? undefined)}
              estimateRowHeight={compact ? 32 : 44}
              emptyState={null}
            />

            {quietCount > 0 && (
              <div className="sk-fleet-quiet">
                {quietCount} more {quietCount === 1 ? "site" : "sites"} in {label} within normal range · verified quiet
              </div>
            )}
          </section>
        );
      })}
      <FleetFooter total={totalSites} shown={rows.length} />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Fleet footer (annotation 6) — with keyboard hints
   ════════════════════════════════════════════════════════════════════════════ */

function FleetFooter({ total, shown }: { total: number; shown: number }): React.ReactNode {
  return (
    <div className="sk-fleet-footer">
      {shown < total
        ? `${total - shown} more · ${total} sites`
        : `${total} ${total === 1 ? "site" : "sites"}`}
      {" · keyboard: / find, j k move, Enter open"}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Cards view (annotation 4)
   ════════════════════════════════════════════════════════════════════════════ */

function CardsView({
  rows,
  grouped,
  group,
  onOpen,
}: {
  rows: Row[];
  grouped: boolean;
  group: string;
  onOpen: (id?: string) => void;
}): React.ReactNode {
  if (grouped) {
    const buckets = group === "health"
      ? groupByHealth(rows).map(([state, groupRows]) => ({ label: state, rows: groupRows, isHealth: true }))
      : groupByDimension(rows, group).map(([label, groupRows]) => ({ label, rows: groupRows, isHealth: false }));

    return (
      <>
        {buckets.map(({ label, rows: groupRows, isHealth }) => (
          <section key={label} className="flex flex-col gap-density-gap">
            <div className="flex items-center gap-density-gap">
              {isHealth ? (
                <HealthBadge state={label as HealthState} showLabel />
              ) : (
                <span className="font-semibold">{label}</span>
              )}
              <span className="text-text-muted text-sm">
                {groupRows.length} {groupRows.length === 1 ? "site" : "sites"}
              </span>
            </div>
            <CardGrid
              items={groupRows}
              getItemKey={(r, i) => r.site_id ?? String(i)}
              minColumnWidth={220}
              emptyState={null}
              renderCard={(r) => <SiteCard row={r} onOpen={onOpen} />}
            />
          </section>
        ))}
      </>
    );
  }
  return (
    <CardGrid
      items={rows}
      getItemKey={(r, i) => r.site_id ?? String(i)}
      minColumnWidth={220}
      emptyState={<EmptyState variant="no_data" title="No sites in scope" />}
      renderCard={(r) => <SiteCard row={r} onOpen={onOpen} />}
    />
  );
}

/** scard left-border colour by health (sk-components.css): ok/partial keep the
 *  default green; warning→warn, fault→fault, everything down→off. */
const HEALTH_SCARD: Record<string, string> = {
  ok: "", partial: "", warning: "warn", fault: "fault", offline: "off", unmonitored: "off",
};

/** One metric in the card's `.m` row: the number, then the unit as its own
 *  sub-label (wireframe 19, cards view). A withheld figure prints its state,
 *  never a zero. */
function MetricCell({ f, unit, scale }: { f?: Figure; unit: string; scale?: boolean }): React.ReactNode {
  if (!f || typeof f.value !== "number") {
    return <div>{withheld("CANNOT_CHECK_FIGURE_ABSENT")}<span>{unit}</span></div>;
  }
  return <div>{formatNum(scale ? scaledFigVal(f) : f.value)}<span>{unit}</span></div>;
}

/** PR-deviation bar: centred on the fleet reference (the `.dev` centre line);
 *  the fill runs toward the site's deviation, left of centre when below fleet. */
function DevBar({ dev }: { dev?: Figure }): React.ReactNode {
  if (!dev || typeof dev.value !== "number") return <div className="dev" />;
  const d = dev.value;
  const w = Math.min(50, Math.abs(d) * 4);
  const left = d < 0 ? 50 - w : 50;
  return <div className="dev"><i style={{ left: `${left}%`, width: `${w}%` }} /></div>;
}

function devText(dev?: Figure): React.ReactNode {
  if (!dev || typeof dev.value !== "number") return withheld("CANNOT_CHECK_FIGURE_ABSENT");
  const d = dev.value;
  return `${d < 0 ? "−" : "+"}${Math.abs(d).toFixed(1)}`;
}

function SiteCard({
  row,
  onOpen,
}: {
  row: Row;
  onOpen: (id?: string) => void;
}): React.ReactNode {
  const capMW = scaledFigVal(row.capacity_dc);
  const noSeries = isNoSeries(row);
  const pathDown = row.freshness?.state === "path_down";
  const scardClass = pathDown || noSeries
    ? "off"
    : (HEALTH_SCARD[(row.health?.state as string) ?? "unmonitored"] ?? "off");
  const seen = row.freshness?.age_s != null ? formatAge(row.freshness.age_s) : "";
  const open = () => onOpen(row.site_id ?? undefined);

  return (
    <div
      className={`scard ${scardClass}`.trim()}
      role="button"
      tabIndex={0}
      style={{ cursor: "pointer", textAlign: "left" }}
      onClick={open}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
    >
      <b>{row.site_id ?? "—"}</b>
      {capMW > 0 && <span className="muted" style={{ marginLeft: 6 }}>{formatNum(capMW)} MWp</span>}

      {noSeries ? (
        <>
          <div className="m"><div className="stateword" style={{ fontSize: 12 }}>Cannot check</div></div>
          <div className="dev" />
          <div className="muted" style={{ marginTop: 4 }}>
            data path down {seen}{alertSuffix(row)}
          </div>
        </>
      ) : (
        <>
          {/* Power now (MW), PR (%), specific yield (kWh/kWp) — the three the
              drawing carries, each with its unit as a sub-label. */}
          <div className="m">
            <MetricCell f={row.power} unit="MW" scale />
            <MetricCell f={row.pr} unit="PR %" />
            <MetricCell f={row.specific_yield} unit="kWh/kWp" />
          </div>
          <DevBar dev={row.pr_deviation} />
          <div className="muted" style={{ marginTop: 4 }}>
            {devText(row.pr_deviation)} vs fleet{alertSuffix(row)}{seen ? ` · ${seen}` : ""}
          </div>
        </>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Heatmap view (wireframe 14, annotations 1–2)
   Quantity selector, neutral ramp, escapee outlines, dotted no-data cells.

   The window is the PAGE's, not the view's. This used to carry its own 7D/30D
   toggle beside the quantity, which meant two range controls on one screen that
   could disagree — and the matrix ignored the one at the top that every other
   view obeys. The columns are now the days of the resolved period.
   ════════════════════════════════════════════════════════════════════════════ */

const HEATMAP_QUANTITIES: { key: string; label: string }[] = [
  { key: "pr", label: "PR Absolute" },
  { key: "pr_stc", label: "PR STC" },
  { key: "availability", label: "Availability" },
  { key: "cuf", label: "CUF" },
  { key: "yield", label: "Specific yield" },
  { key: "energy", label: "Energy" },
];

function HeatmapView({ rows: _rows, period, from, to }: {
  rows: Row[]; period?: string; from?: string; to?: string;
}): React.ReactNode {
  const [metric, setMetric] = useState<string>("pr");

  // Custom bounds only mean anything for a custom window, which is how every
  // other read on this page sends them.
  const q = useGetV1Heatmap({
    metric: metric as GetV1HeatmapMetric,
    period,
    ...(period === "custom" ? { from, to } : {}),
  });

  if (q.isLoading) return <Skeleton variant="row" lines={6} />;
  if (q.isError || !q.data || q.data.status !== 200) {
    return <EmptyState variant="no_data" title="Heat map unavailable" />;
  }

  const d = q.data.data.data;
  const cols = d.cols;
  const rowLabels = d.rows.map((r) => r.label ?? r.site ?? "—");
  const thresholdLow = d.threshold_low ?? 0;
  const values = d.rows.map((r) =>
    (r.cells ?? []).map((c) => {
      if (c.state === "NO_DATA" || c.value == null) return null;
      return c.value;
    }),
  );
  const noDataMask = d.rows.map((r) =>
    (r.cells ?? []).map((c) => c.state === "NO_DATA"),
  );
  // A day in progress: read live, because it has no completed row yet. It is a
  // real figure and it is drawn, but it is marked — it will still move.
  const provisionalMask = d.rows.map((r) =>
    (r.cells ?? []).map((c) => (c.state as string) === "PROVISIONAL"),
  );

  const metricLabel = HEATMAP_QUANTITIES.find((h) => h.key === metric)?.label ?? d.metric_label ?? d.metric;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* ── Toolbar: Quantity selector + ramp legend. The range lives at the top
             of the page, where every other view reads it from. ── */}
      <div className="sk-heatmap-toolbar">
        <span className="sk-heatmap-toolbar__label">Quantity</span>
        <select
          className="sk-groupby__select"
          value={metric}
          onChange={(e) => setMetric(e.target.value)}
          aria-label="Heat map quantity"
        >
          {HEATMAP_QUANTITIES.map((h) => (
            <option key={h.key} value={h.key}>{h.label}</option>
          ))}
        </select>

        <div className="sk-heatmap-ramp" aria-label="Colour ramp: low to high">
          <span className="text-text-muted" style={{ fontSize: 10 }}>low</span>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <span key={i} className={`sk-heatmap-ramp__swatch sk-heatmap-ramp__swatch--${i}`} />
          ))}
          <span className="text-text-muted" style={{ fontSize: 10 }}>high</span>
        </div>
      </div>

      {/* A heat map is day-against-day. The live day has no completed KPI in it
          — by definition, not by failure — so one empty column is said in words
          rather than drawn as a grid of dots. */}
      {cols.length <= 1 && values.every((r) => r.every((v) => v === null)) ? (
        <div className="text-text-muted" style={{ fontSize: 11 }}>
          A heat map compares completed days, and today has not been computed yet:
          the engine produces a day&rsquo;s KPI once that day has ended. Choose 7D
          or 30D, or a custom range, to see the matrix.
        </div>
      ) : null}

      {/* ── Caption: the window the matrix was actually drawn over. ── */}
      <div className="text-text-muted" style={{ fontSize: 11 }}>
        Site · {metricLabel} · {cols.length === 1 ? "1 day" : `${cols.length} days`}
        {d.cols.length > 0 ? ` · ${cols[0]} to ${cols[cols.length - 1]}` : ""}
        {/* A rolling window carries today beyond the days the engine has closed,
            so the column count is one more than the range names. Say so, rather
            than leaving "7D" over eight columns to be puzzled at. */}
        {provisionalMask.some((r) => r.some(Boolean))
          ? ", the last of them today and still running" : ""}
      </div>

      {/* ── Enhanced Heatmap with escapee outlines + dotted no-data ── */}
      <div style={{ overflowX: "auto" }}>
        <HeatmapTable
          rows={rowLabels}
          cols={cols}
          values={values}
          noDataMask={noDataMask}
          provisionalMask={provisionalMask}
          thresholdLow={thresholdLow}
          unit={d.unit ?? ""}
          metricLabel={metricLabel}
        />
      </div>

      {/* ── Footer note ── */}
      <p className="text-text-muted" style={{ fontSize: 11 }}>
        Rows are sites in the grid's order, columns are days. Outline marks a day below
        {thresholdLow > 0 ? ` ${thresholdLow}` : " threshold"} ({d.unit ?? "%"});
        a dotted cell is a day with no admitted intervals; an underlined figure is
        today, read live because the day has not ended and still moving.
        Hover for the figure with basis and coverage; click a row for the site at that period.
      </p>
    </div>
  );
}

function HeatmapTable({
  rows,
  cols,
  values,
  noDataMask,
  provisionalMask,
  thresholdLow,
  unit,
  metricLabel,
}: {
  rows: string[];
  cols: string[];
  values: (number | null)[][];
  noDataMask: boolean[][];
  provisionalMask?: boolean[][];
  thresholdLow: number;
  unit: string;
  metricLabel: string;
}): React.ReactNode {
  const allValues = values.flat().filter((v): v is number => v !== null);
  const maxVal = Math.max(1, ...allValues);

  function rampIdx(v: number): number {
    const ratio = v / maxVal;
    return Math.min(5, Math.floor(ratio * 6));
  }

  const colLabels = cols.map((c) => {
    const d = new Date(c);
    return isNaN(d.getTime()) ? c : String(d.getDate()).padStart(2, "0");
  });

  return (
    <table
      className="sk-heatmap-table"
      aria-label={`${metricLabel} heat map`}
    >
      <thead>
        <tr>
          <td />
          {colLabels.map((c, ci) => (
            <th key={ci} scope="col">{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((rowLabel, ri) => (
          <tr key={ri}>
            <th scope="row">{rowLabel}</th>
            {colLabels.map((_c, ci) => {
              const v = values[ri]?.[ci] ?? null;
              const isNoData = noDataMask[ri]?.[ci] ?? false;
              const isProvisional = provisionalMask?.[ri]?.[ci] ?? false;
              const isEscapee = v !== null && thresholdLow > 0 && v < thresholdLow;

              let className = "sk-heatmap-cell";
              if (isEscapee) className += " sk-heatmap-cell--escapee";
              if (isNoData) className += " sk-heatmap-cell--nodata";
              if (isProvisional) className += " sk-heatmap-cell--provisional";
              if (v !== null) className += ` sk-heatmap-cell--r${rampIdx(v)}`;

              const reading = v !== null ? `${v} ${unit}` : "no data";
              return (
                <td
                  key={ci}
                  className={className}
                  title={`${rowLabel} / ${cols[ci]}: ${reading}`
                         + (isProvisional ? " · provisional, the day is still running" : "")}
                >
                  {isNoData ? (
                    <span className="sk-heatmap-cell__dot">·</span>
                  ) : v !== null ? (
                    v
                  ) : (
                    <span className="sk-heatmap-cell__dot">·</span>
                  )}
                  <span className="sr-only">
                    {rowLabel} / {cols[ci]}: {v === null ? "no data" : `${v} ${unit}`}
                    {isProvisional ? ", provisional, the day is still running" : ""}
                  </span>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Table columns — Monitoring set (annotation 2, wireframe 08)
   Units in headers, bare numbers in cells. No-series → state word + reason.
   ════════════════════════════════════════════════════════════════════════════ */

// `tkey` wraps the label in a glossary <Term> (UX-REV-001 U3): a non-specialist
// can hover a header like CUF or SP. YIELD and read its plain meaning.
function colHeader(label: string, unit?: string, tkey?: string): React.ReactNode {
  return (
    <span>
      {tkey ? <Term tkey={tkey}>{label}</Term> : label}
      {unit && <><br /><span className="text-text-muted" style={{ fontSize: 10, fontWeight: 400, textTransform: "none" }}>{unit}</span></>}
    </span>
  );
}

function makeColumns(
  hasValueLens: boolean,
  selected: Set<string>,
  onToggle: (id: string) => void,
  onToggleAll: () => void,
  totalRows: number,
  visibleColIds?: Set<string>,
): ColumnDef<Row, unknown>[] {
  const cols: ColumnDef<Row, unknown>[] = [
    {
      id: "select",
      header: () => (
        <input
          type="checkbox"
          checked={selected.size === totalRows && totalRows > 0}
          onChange={onToggleAll}
          aria-label="Select all sites"
        />
      ),
      cell: (c) => (
        <input
          type="checkbox"
          checked={selected.has(c.row.original.site_id ?? "")}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            onToggle(c.row.original.site_id ?? "");
          }}
          aria-label={`Select ${c.row.original.site_id ?? "site"}`}
        />
      ),
    },
    {
      accessorKey: "site_id",
      header: "SITE",
      cell: (c) => {
        const r = c.row.original;
        const cap = scaledFigVal(r.capacity_dc);
        return (
          <span>
            <span className="font-semibold">{r.site_id ?? "—"}</span>
            {cap > 0 && <span className="text-text-muted" style={{ fontSize: 10, marginLeft: 4 }}>{formatNum(cap)} MWp</span>}
          </span>
        );
      },
    },
    {
      id: "health",
      header: "HEALTH",
      cell: (c) => {
        const h = c.row.original.health;
        return (
          <HealthBadge
            state={(h?.state as HealthState) ?? "unmonitored"}
            modifiers={h?.modifiers as HealthModifier[] | undefined}
            showLabel
          />
        );
      },
    },
    {
      id: "power",
      header: () => colHeader("POWER", "MW"),
      cell: (c) => {
        const r = c.row.original;
        if (isNoSeries(r)) return noSeriesCell(r);
        return scaledFigCell(r.power);
      },
    },
    {
      id: "capacity",
      header: () => colHeader("CAPACITY", "MWp"),
      cell: (c) => scaledFigCell(c.row.original.capacity_dc),
    },
    {
      id: "energy_today",
      header: () => colHeader("ENERGY", "MWh"),
      cell: (c) => {
        const r = c.row.original;
        if (isNoSeries(r)) return noSeriesCell(r);
        return scaledFigCell(r.energy_today);
      },
    },
    {
      id: "basis",
      header: "BASIS",
      cell: (c) => {
        const f = c.row.original.energy_today;
        if (!f) return withheld("CANNOT_CHECK_NO_ENERGY_FIGURE");
        if (f.state === "CANNOT_CHECK" || f.state === "NOT_MEASURED") {
          return <StateWord state={f.state} reason={f.reason} />;
        }
        const b = f.basis;
        return b
          ? <span className="sk-chip sk-chip--basis">{basisLabel(b)}</span>
          : withheld("CANNOT_CHECK_NO_DESIGNATED_BASIS");
      },
    },
    {
      id: "sp_yield",
      header: () => colHeader("SP. YIELD", "kWh/kWp", "specific_yield"),
      cell: (c) => noSeriesOrFig(c.row.original, c.row.original.specific_yield),
    },
    {
      id: "pr",
      header: () => colHeader("PR · ABS", "%", "pr"),
      cell: (c) => {
        const r = c.row.original;
        if (isNoSeries(r)) return noSeriesCell(r);
        return figCell(r.pr);
      },
    },
    // No "30 D" PR sparkline column: the plant-list contract carries no PR series,
    // so it rendered an empty sparkline under a populated header on every row.
    {
      id: "cuf",
      header: () => colHeader("CUF", "%", "cuf"),
      cell: (c) => noSeriesOrFig(c.row.original, c.row.original.cuf),
    },
    {
      id: "availability",
      header: () => colHeader("AVAIL.", "%", "technical_availability"),
      cell: (c) => noSeriesOrFig(c.row.original, c.row.original.availability),
    },
    {
      id: "insolation",
      header: () => colHeader("INSOL.", "kWh/m²"),
      cell: (c) => noSeriesOrFig(c.row.original, c.row.original.insolation),
    },
    // No MODULE °C / INV. reporting columns: neither field exists on the contract.
    // They stay in the columns menu as disabled entries (see COL_DEFS).
    {
      id: "coverage",
      header: () => colHeader("COVER.", "%"),
      cell: (c) => {
        const r = c.row.original;
        if (isNoSeries(r)) return noSeriesCell(r);
        return figCell(r.coverage);
      },
    },
    {
      id: "alerts",
      header: () => colHeader("ALERTS", "open"),
      // A missing count is not "no alerts" — it means the detection read failed.
      cell: (c) =>
        typeof c.row.original.open_interventions === "number"
          ? <span>{c.row.original.open_interventions}</span>
          : withheld("CANNOT_CHECK_NO_INTERVENTION_COUNT"),
    },
    {
      id: "seen",
      header: "SEEN",
      cell: (c) => {
        const age = c.row.original.freshness?.age_s;
        if (age == null) return withheld("CANNOT_CHECK_NO_LIVE_PATH");
        return <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatAge(age)}</span>;
      },
    },
  ];

  if (hasValueLens) {
    cols.push({
      id: "value",
      header: "Value",
      cell: (c) =>
        c.row.original.money_at_stake ? (
          <Money money={c.row.original.money_at_stake} />
        ) : (
          withheld("CANNOT_CHECK_NO_VALUE_AT_STAKE")
        ),
    });
  }

  if (!visibleColIds) return cols;
  return cols.filter((c) => visibleColIds.has(String(c.id ?? (c as { accessorKey?: string }).accessorKey ?? "")));
}

/* ════════════════════════════════════════════════════════════════════════════
   Helpers
   ════════════════════════════════════════════════════════════════════════════ */

/**
 * The withheld treatment for a cell with no figure at all: the state word with a
 * true reason, never a bare dash and never a zero (UX-003 §3.1, ruling §3).
 */
function withheld(template: string): React.ReactNode {
  return <StateWord state="CANNOT_CHECK" reason={{ template }} />;
}

/**
 * The card's alert suffix. An absent count is stated as withheld — it is NOT the
 * same thing as "no alerts", which is what `?? 0` used to assert.
 */
function alertSuffix(row: Row): React.ReactNode {
  const n = row.open_interventions;
  if (typeof n !== "number") {
    return (
      <>
        {" · alerts "}
        {withheld("CANNOT_CHECK_NO_INTERVENTION_COUNT")}
      </>
    );
  }
  if (n === 0) return null;
  return ` · ${n} alert${n === 1 ? "" : "s"}`;
}

function isNoSeries(r: Row): boolean {
  return r.energy_today?.state === "CANNOT_CHECK" || r.energy_today?.state === "NOT_MEASURED";
}

function noSeriesCell(r: Row): React.ReactNode {
  const reason = r.energy_today?.reason ?? r.freshness?.hop ?? "";
  if (!reason) return <span className="text-text-muted">Cannot check</span>;
  return (
    <span className="text-text-muted" style={{ fontSize: 11 }}>
      Cannot check · {typeof reason === "string" ? reason : "no packets"}
    </span>
  );
}

function noSeriesOrFig(r: Row, f?: Figure): React.ReactNode {
  if (isNoSeries(r)) return noSeriesCell(r);
  if (!f) return withheld("CANNOT_CHECK_FIGURE_ABSENT");
  return <Metric figure={f} showBasis={false} className="sk-metric--table" />;
}

function sortCmp(a: Row, b: Row, key: FleetSort): number {
  if (key === "site") return (a.site_id ?? "").localeCompare(b.site_id ?? "");
  const pick = (r: Row) => {
    switch (key) {
      case "at_stake": return figVal(r.energy_at_stake);
      case "energy": return figVal(r.energy_today);
      case "pr": return figVal(r.pr);
      case "coverage": return figVal(r.coverage);
      default: return figVal(r.energy_at_stake);
    }
  };
  return pick(b) - pick(a);
}

function groupByHealth(rows: Row[]): [HealthState, Row[]][] {
  const map = new Map<HealthState, Row[]>();
  for (const r of rows) {
    const s = (r.health?.state as HealthState) ?? "unmonitored";
    (map.get(s) ?? map.set(s, []).get(s)!).push(r);
  }
  return HEALTH_ORDER.filter((s) => map.has(s)).map((s) => [s, map.get(s)!]);
}

function groupByDimension(rows: Row[], dim: string): [string, Row[]][] {
  const map = new Map<string, Row[]>();
  for (const r of rows) {
    const key = r.grouping_values?.[dim] ?? "Other";
    (map.get(key) ?? map.set(key, []).get(key)!).push(r);
  }
  return Array.from(map.entries());
}

function figVal(f?: Figure): number {
  return typeof f?.value === "number" ? f.value : -1;
}

/** A figure as a plain CSV number: the raw value with NO thousands separator, so
 *  Excel reads it as one number instead of splitting "8,400" into two columns
 *  (QA PL1). Withheld → empty, never 0 (ruling §3.2). */
function csvNum(f?: Figure): string {
  if (!f || typeof f.value !== "number") return "";
  return String(Math.round(f.value * 100) / 100);
}

/** One RFC 4180 CSV field: always quoted, with internal quotes doubled. Quoting
 *  unconditionally means a comma in any value (a formatted number, a label) can
 *  never bleed into the next column. */
function csvField(s: string | null | undefined): string {
  return `"${String(s ?? "").replace(/"/g, '""')}"`;
}

function figCell(f?: Figure): React.ReactNode {
  if (!f) return withheld("CANNOT_CHECK_FIGURE_ABSENT");
  return <Metric figure={f} showBasis={false} className="sk-metric--table" />;
}

function scaledFigCell(f?: Figure): React.ReactNode {
  if (!f) return withheld("CANNOT_CHECK_FIGURE_ABSENT");
  const scaled = scaleToMega(f);
  return <Metric figure={scaled} showBasis={false} className="sk-metric--table" />;
}

function scaledFigVal(f?: Figure): number {
  if (!f || typeof f.value !== "number") return 0;
  const rule = f.unit ? UNIT_SCALE[f.unit] : undefined;
  return rule ? f.value / rule.divisor : f.value;
}

const UNIT_SCALE: Record<string, { divisor: number; unit: string }> = {
  kW: { divisor: 1000, unit: "MWp" },
  kWp: { divisor: 1000, unit: "MWp" },
  kWh: { divisor: 1000, unit: "MWh" },
  W: { divisor: 1_000_000, unit: "MWp" },
  Wh: { divisor: 1_000_000, unit: "MWh" },
};

function scaleToMega(f: Figure): Figure {
  const v = typeof f.value === "number" ? f.value : 0;
  const rule = f.unit ? UNIT_SCALE[f.unit] : undefined;
  if (!rule || v === 0) return f;
  return { ...f, value: v / rule.divisor, unit: rule.unit };
}

function formatNum(n: number): string {
  return n >= 100 ? Math.round(n).toLocaleString() : n.toFixed(1);
}
